<script setup lang="ts">
import { vIntersectionObserver } from "@vueuse/components";

const props = defineProps({
  document: {
    type: Object,
    required: true,
  },
});

const { localeProperties } = useI18n();
const { client } = usePrismic();

const { data: bims, error } = await useAsyncData(
  `BimList-${localeProperties.value.language}`,
  async () => {
    const bims = await client.getAllByType("bim_page", {
      lang: localeProperties.value.language,
      pageSize: 100,
    });

    return bims.map((bim) => ({
      title: bim.data.title,
      description: bim.data.description,
      image: bim.data.image,
      lang: bim.lang,
      uid: bim.uid,
      id: bim.id,
      type: bim.type,
      location: bim.data.location || "",
      startDate: bim.data.start_date
        ? new Date(bim.data.start_date as string).getFullYear()
        : "",
    }));
  },
);

if (error.value) {
  console.log(error.value);
  throw createError({ statusCode: 404, message: "Page not found" });
}

const onElementVisibility = ([
  { isIntersecting, target },
]: IntersectionObserverEntry[]) => {
  if (isIntersecting) {
    target.classList.add("animate-fadeIn");
  } else {
    target.classList.remove("animate-fadeIn");
  }
};
</script>

<template>
  <main>
    <section
      class="mx-auto flex h-full w-full max-w-[1024px] flex-col items-center px-6 pb-10 pt-40 -sm:pt-28"
    >
      <RichText
        :text="document.data.title"
        class="text-center text-3xl font-extralight uppercase text-black"
      />
      <hr class="my-6 w-[50px] border-[0.5px] border-gray-800" />
    </section>

    <section class="mx-auto mb-20 mt-10 w-full max-w-[1024px] px-6 -sm:px-4">
      <TransitionGroup
        tag="ul"
        class="grid grid-cols-3 gap-12 -md:grid-cols-2 -md:gap-6 -sm:grid-cols-1 -sm:gap-12"
        name="list"
      >
        <li
          v-for="(bim, idx) in bims"
          :key="idx"
          class="opacity-0 [&_img]:hover:scale-110"
          v-intersection-observer="onElementVisibility"
        >
          <PLink
            :link-object="{
              link_type: 'Document',
              type: 'bim_page',
              lang: bim.lang,
              id: bim.id,
              uid: bim.uid,
            }"
          >
            <div class="h-[220px] w-auto overflow-hidden">
              <PImage
                :image-object="bim.image"
                lazy
                class="h-full w-full object-cover transition-transform duration-500"
              />
            </div>
          </PLink>

          <div class="mt-5 flex flex-col items-center justify-center gap-5">
            <RichText
              :text="bim.title"
              class="text-center text-lg font-extralight"
            />
            <hr class="w-[30px] border-[0.5px] border-black" />
            <p class="text-center text-xs font-extralight text-gray-600">
              {{ [bim.location, bim.startDate].filter((a) => !!a).join(", ") }}
            </p>
          </div>
        </li>
      </TransitionGroup>
    </section>
  </main>
</template>
<style lang="scss" scoped>
.animate-fadeIn {
  animation: fade-in 0.7s ease-in-out forwards;
}

@keyframes fade-in {
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
}

.list-leave-from {
  display: none;
}
</style>
